import { HubConnection, HubConnectionState } from '@microsoft/signalr'
import { tryRefreshToken } from './authRefresh'

let visibilityHandler: (() => void) | null = null
let resumeHandler: (() => void) | null = null

const sendPing = (connection: HubConnection): void => {
  if (connection.state !== HubConnectionState.Connected) return
  try {
    const internal = connection as unknown as Record<string, unknown>
    const cachedPing = internal._cachedPingMessage
    const send = internal._sendMessage
    if (cachedPing && typeof send === 'function') {
      const sendFn = send as (msg: unknown) => Promise<void>
      // Fire-and-forget: a failed ping just means the socket is already gone,
      // which the built-in keepalive / reconnect will handle.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      void sendFn.call(connection, cachedPing).catch(() => {})
    }
  } catch {
    /* swallow — built-in keepalive is the fallback */
  }
}

const onVisibilityChange = (
  connection: HubConnection,
  onWakeReload: () => void,
  isTokenExpired: () => boolean
): void => {
  if (document.visibilityState !== 'visible') return
  /* On wake, anything short of Connected (Disconnected OR an in-flight
     auto-reconnect) means the socket dropped during the freeze. Reload ONLY if
     the token also expired (reconnect can't fix). Let auto/stateful reconnect
     recover if token is valid so the user keeps their working orders.
   */
  if (connection.state !== HubConnectionState.Connected) {
    if (isTokenExpired()) {
      tryRefreshToken()
        .then(() => sendPing(connection))
        .catch(() => onWakeReload())
    }
    return
  }
  sendPing(connection)
}

export const startKeepAlive = (
  connection: HubConnection,
  onWakeReload: () => void,
  isTokenExpired: () => boolean
): void => {
  stopKeepAlive()
  visibilityHandler = () =>
    onVisibilityChange(connection, onWakeReload, isTokenExpired)
  document.addEventListener('visibilitychange', visibilityHandler)
  /* The `resume` Page Lifecycle event fires when Edge unfreezes a slept tab,
     even while still backgrounded. At that point the hub still reads Connected
     (the close hasn't been processed yet), so the visibilitychange state check can't catch it.
     A resume means we were frozen long enough to drop pings,
     but NOT necessarily long enough to expire the token.

     So: if the token expired, reload immediately to re-bootstrap auth.
     Edge keeps the tab awake only ~1s on resume, so a delayed reload would be
     re-frozen before it fires. If the token is still valid, nudge the
     (stale-Connected) transport with a ping so SignalR notices the dead socket
     and reconnects in place — no reload, orders kept.
   */
  resumeHandler = () => {
    if (isTokenExpired()) {
      onWakeReload()
    } else {
      sendPing(connection)
    }
  }
  document.addEventListener('resume', resumeHandler)
}

export const stopKeepAlive = (): void => {
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  if (resumeHandler) {
    document.removeEventListener('resume', resumeHandler)
    resumeHandler = null
  }
}

export { sendPing as _sendPingForTest }
